import {
  IntegrationError,
  IntegrationErrorStatus,
  IntegrationErrorsSummary,
  RawIntegrationErrorItem,
} from '../../@types';
import { PeriodoModo, StatusFilterValue } from './constants';

interface PeriodoAplicado {
  modo: PeriodoModo;
  range: Array<Date | string>;
}

interface BuildIntegrationErrorsRequestParamsInput {
  textInput: string;
  statusFilter: StatusFilterValue;
  periodoAplicado: PeriodoAplicado;
}

interface IntegrationErrorsRequestParams {
  search?: string;
  status?: string;
  from?: string;
  to?: string;
}

export function getPeriodoLabel(periodoAplicado: PeriodoAplicado): string {
  if (periodoAplicado.modo === '10dias') return 'Últ. 10 dias';
  if (periodoAplicado.modo === '15dias') return 'Últ. 15 dias';

  if (
    periodoAplicado.modo === 'customizado' &&
    periodoAplicado.range.length === 2
  ) {
    const currentYear = new Date().getFullYear();
    const formatDate = (value: Date | string) => {
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return null;
      }

      const isCurrentYear = date.getFullYear() === currentYear;

      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        ...(isCurrentYear ? {} : { year: 'numeric' }),
      });
    };

    const startLabel = formatDate(periodoAplicado.range[0]);
    const endLabel = formatDate(periodoAplicado.range[1]);

    if (!startLabel || !endLabel) {
      return 'Período';
    }

    return `${startLabel} – ${endLabel}`;
  }

  return 'Período';
}

export function getPeriodoRange(periodoAplicado: PeriodoAplicado): {
  start: Date;
  end: Date;
} | null {
  if (!periodoAplicado.modo) {
    return null;
  }

  let start: Date;
  let end: Date;

  if (periodoAplicado.modo === '10dias') {
    end = new Date();
    start = new Date();
    start.setDate(start.getDate() - 10);
  } else if (periodoAplicado.modo === '15dias') {
    end = new Date();
    start = new Date();
    start.setDate(start.getDate() - 15);
  } else if (
    periodoAplicado.modo === 'customizado' &&
    periodoAplicado.range.length === 2
  ) {
    start = new Date(periodoAplicado.range[0]);
    end = new Date(periodoAplicado.range[1]);
  } else {
    start = new Date(0);
    end = new Date();
  }

  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

const MIN_SEARCH_LENGTH = 3;

function sanitizeSearchText(text: string): string {
  return text
    .trim()
    .replace(/<[^>]*>/g, '')
    .replace(/[<>"'`]/g, '');
}

export function buildIntegrationErrorsRequestParams({
  textInput,
  statusFilter,
  periodoAplicado,
}: BuildIntegrationErrorsRequestParamsInput): IntegrationErrorsRequestParams {
  const params: IntegrationErrorsRequestParams = {};

  const search = sanitizeSearchText(textInput);

  if (search.length >= MIN_SEARCH_LENGTH) {
    params.search = search;
  }

  if (statusFilter !== 'todos') {
    params.status = statusFilter;
  }

  const periodoRange = getPeriodoRange(periodoAplicado);

  if (periodoRange) {
    params.from = periodoRange.start.toISOString();
    params.to = periodoRange.end.toISOString();
  }

  return params;
}

const MODEL_HUMANIZED: Record<string, string> = {
  Aplicacao: 'Aplicação',
  AplicacaoItem: 'Aplicação',
  Gleba: 'Talhão',
  Plantio: 'Plantio',
  PrestacaoServico: 'Serviço de Máquina',
  RegistroChuva: 'Registro de Chuva',
  TratamentoSemente: 'Tratamento de Semente',
  TransferenciaEstoque: 'Transferência de Estoque',
  TransferenciaEstoqueItem: 'Transferência de Estoque',
  MovimentoEstoqueTransferencia: 'Tratamento de Semente',
  MovimentoEstoqueTransferenciaItem: 'Tratamento de Semente',
  Usuario: 'Usuário',
};

const DEFAULT_MODEL_LABEL = 'Sincronização AgroGestão';

export const DEFAULT_SYNC_AGROGESTAO_SUMMARY: IntegrationErrorsSummary = {
  total: 0,
  byStatus: {},
};

export function mapSyncAgrogestaoStatus(
  status?: string | null,
): IntegrationErrorStatus {
  switch ((status ?? '').toLowerCase()) {
    case 'erro':
      return 'erro';
    case 'processando':
    case 'enviado':
      return 'processando';
    case 'pendente':
      return 'pendente';
    case 'confirmado':
      return 'confirmado';
    default:
      return 'erro';
  }
}

export function humanizeSyncAgrogestaoModel(model?: string | null): string {
  const normalizedModel = model?.trim();

  if (!normalizedModel) {
    return DEFAULT_MODEL_LABEL;
  }

  return MODEL_HUMANIZED[normalizedModel] ?? normalizedModel;
}

export function mapSyncAgrogestaoItem(
  rawItem: RawIntegrationErrorItem,
): IntegrationError {
  return {
    id: rawItem.id,
    propriedade: rawItem.propriedadeDescricao?.trim() || 'Sem propriedade',
    entidade: humanizeSyncAgrogestaoModel(rawItem.model),
    model: rawItem.model?.trim() ?? null,
    idLocal: rawItem.idLocal?.trim() ?? null,
    origem: rawItem.origem?.trim() ?? null,
    createdAt:
      rawItem.dataOperacao ?? rawItem.createdAt ?? new Date(0).toISOString(),
    status: mapSyncAgrogestaoStatus(rawItem.status ?? rawItem.situacao),
    mensagemErro: rawItem.mensagemErro ?? undefined,
    codigoErro: rawItem.codigoErro ?? null,
  };
}
